import styled, { keyframes, css } from 'styled-components';
import { Button, Card, Tag, Drawer, Modal } from 'antd';
import { COLORS } from '../../constants/colors';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const waveform = keyframes`
  0%, 100% { height: 4px; }
  50% { height: 16px; }
`;

// ============================================
// PAGE CONTAINER
// ============================================

export const PageContainer = styled.div`
  padding: 0;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const PageContent = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

// ============================================
// STATS CARDS
// ============================================

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

export const StatCard = styled(Card)`
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  .ant-card-body {
    padding: 20px;
  }
  
  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 12px;
    
    ${props => {
      switch (props.$variant) {
        case 'primary':
          return css`
            background: rgba(24, 144, 255, 0.1);
            color: #1890ff;
          `;
        case 'success':
          return css`
            background: rgba(82, 196, 26, 0.1);
            color: #52c41a;
          `;
        case 'warning':
          return css`
            background: rgba(250, 173, 20, 0.1);
            color: #faad14;
          `;
        case 'danger':
          return css`
            background: rgba(255, 77, 79, 0.1);
            color: #ff4d4f;
          `;
        default:
          return css`
            background: rgba(0, 0, 0, 0.04);
            color: #595959;
          `;
      }
    }}
  }
  
  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #262626;
    line-height: 1.2;
  }
  
  .stat-label {
    font-size: 14px;
    color: #8c8c8c;
    margin-top: 4px;
  }
  
  .stat-change {
    font-size: 13px;
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
    
    &.positive {
      color: #52c41a;
    }
    
    &.negative {
      color: #ff4d4f;
    }
  }
`;

// ============================================
// FILTERS BAR
// ============================================

export const FiltersCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
`;

export const FiltersBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 12px;
  flex-wrap: wrap;
  
  .filter-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .filter-label {
    font-size: 13px;
    color: #8c8c8c;
    white-space: nowrap;
  }
  
  .filter-divider {
    width: 1px;
    height: 24px;
    background: #e8e8e8;
    margin: 0 8px;
  }

  /* Standardize all buttons to 40px (except icon-btn) */
  .ant-btn:not(.icon-btn) {
    height: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    font-size: 14px !important;
    border-radius: 8px !important;
  }

  /* Standardize selects to 40px */
  .ant-select {
    height: 40px !important;

    .ant-select-selector {
      height: 40px !important;
      min-height: 40px !important;
      border-radius: 8px !important;
      font-size: 14px !important;
      display: flex !important;
      align-items: center !important;
    }
  }

  /* Standardize inputs to 40px */
  .ant-input,
  .ant-input-affix-wrapper {
    height: 40px !important;
    min-height: 40px !important;
    border-radius: 8px !important;
    font-size: 14px !important;
  }

  /* Standardize date pickers to 40px */
  .ant-picker {
    height: 40px !important;
    min-height: 40px !important;
    border-radius: 8px !important;
    font-size: 14px !important;
  }
  
  .icon-btn {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    padding: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px !important;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    
    .filter-group {
      flex-wrap: wrap;
    }
  }
`;

export const QuickFilterGroup = styled.div`
  display: flex;
  gap: 8px;
  
  .ant-btn {
    border-radius: 8px;
  }
`;

export const SearchInput = styled.div`
  flex: 1;
  min-width: 200px;
  max-width: 300px;
  
  .ant-input-affix-wrapper {
    border-radius: 8px;
  }
`;

// ============================================
// TABS
// ============================================

export const TabsContainer = styled.div`
  border-bottom: 1px solid #f0f0f0;
  
  .ant-tabs {
    .ant-tabs-nav {
      margin: 0;
      padding: 0 20px;
      
      &::before {
        border: none;
      }
    }
    
    .ant-tabs-tab {
      padding: 16px 4px;
      font-weight: 500;
      
      &.ant-tabs-tab-active {
        .ant-tabs-tab-btn {
          color: ${COLORS.$primaryBlue || '#1890ff'};
        }
      }
    }
    
    .ant-tabs-ink-bar {
      background: ${COLORS.$primaryBlue || '#1890ff'};
      height: 3px;
      border-radius: 3px 3px 0 0;
    }
  }
`;

export const TabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  margin-left: 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$active ? '#1890ff' : '#f0f0f0'};
  color: ${props => props.$active ? '#fff' : '#8c8c8c'};
`;

// ============================================
// TABLE STYLES
// ============================================

export const TableContainer = styled.div`
  .ant-table {
    .ant-table-thead > tr > th {
      background: #fafafa;
      font-weight: 600;
      font-size: 13px;
      color: #595959;
      padding: 14px 16px;
      border-bottom: 1px solid #f0f0f0;
    }
    
    .ant-table-tbody > tr > td {
      padding: 14px 16px;
      border-bottom: 1px solid #f5f5f5;
    }
    
    .ant-table-tbody > tr:hover > td {
      background: #fafafa;
    }
    
    .ant-table-tbody > tr.ant-table-row-selected > td {
      background: rgba(24, 144, 255, 0.05);
    }
  }
  
  /* Compact pagination */
  .ant-pagination {
    padding: 12px 16px;
    margin: 0;
    gap: 8px;
    
    .ant-pagination-total-text {
      font-size: 13px;
    }
    
    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid #e8e8e8;
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      
      a {
        font-size: 13px;
        font-weight: 600;
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid #e8e8e8;
      min-width: 32px !important;
      height: 32px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .anticon {
        font-size: 14px;
      }
    }
    
    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid #e8e8e8;
      min-width: 32px !important;
      height: 32px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .anticon {
        font-size: 14px;
      }
    }
    
    .ant-pagination-options {
      .ant-pagination-options-size-changer {
        .ant-select-selector {
          height: 32px !important;
          min-height: 32px !important;
          border-radius: 16px !important;
          border: 1.5px solid #e8e8e8 !important;
          font-size: 13px !important;
          display: flex !important;
          align-items: center !important;
        }
      }
      
      .ant-pagination-options-quick-jumper {
        font-size: 13px;
        
        .ant-input {
          height: 32px !important;
          min-height: 32px !important;
          width: 50px !important;
          border-radius: 16px !important;
          border: 1.5px solid #e8e8e8 !important;
          font-size: 13px;
          text-align: center;
        }
      }
    }
  }
`;

// ============================================
// CALL ROW COMPONENTS
// ============================================

export const DirectionCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  .direction-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    
    ${props => props.$direction === 'inbound' ? css`
      background: rgba(82, 196, 26, 0.1);
      color: #52c41a;
    ` : css`
      background: rgba(24, 144, 255, 0.1);
      color: #1890ff;
    `}
  }
`;

export const PhoneNumberCell = styled.div`
  .phone-number {
    font-weight: 500;
    color: #262626;
    font-size: 14px;
  }
  
  .contact-name {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 2px;
  }
`;

export const DurationBadge = styled(Tag)`
  border-radius: 6px;
  border: none;
  font-weight: 600;
  font-size: 12px;
  padding: 2px 8px;
  
  ${props => {
    const duration = props.$duration || 0;
    if (duration < 30) {
      return css`
        background: rgba(250, 173, 20, 0.1);
        color: #d48806;
      `;
    } else if (duration < 120) {
      return css`
        background: rgba(24, 144, 255, 0.1);
        color: #1890ff;
      `;
    } else {
      return css`
        background: rgba(82, 196, 26, 0.1);
        color: #52c41a;
      `;
    }
  }}
`;

export const StatusBadge = styled(Tag)`
  border-radius: 6px;
  border: none;
  font-weight: 500;
  font-size: 12px;
  padding: 4px 10px;
  
  ${props => {
    switch (props.$status) {
      case 'completed':
      case 'in-progress':
        return css`
          background: rgba(82, 196, 26, 0.1);
          color: #52c41a;
        `;
      case 'ringing':
      case 'initiated':
        return css`
          background: rgba(24, 144, 255, 0.1);
          color: #1890ff;
        `;
      case 'no-answer':
      case 'busy':
        return css`
          background: rgba(250, 173, 20, 0.1);
          color: #d48806;
        `;
      case 'failed':
      case 'canceled':
        return css`
          background: rgba(255, 77, 79, 0.1);
          color: #ff4d4f;
        `;
      default:
        return css`
          background: rgba(0, 0, 0, 0.04);
          color: #595959;
        `;
    }
  }}
`;

export const RecordingCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${props => props.$hasRecording ? 'pointer' : 'default'};
  
  .waveform {
    display: flex;
    align-items: center;
    gap: 2px;
    height: 20px;
    
    .bar {
      width: 3px;
      background: ${props => props.$hasRecording ? '#1890ff' : '#d9d9d9'};
      border-radius: 2px;
      
      ${props => props.$hasRecording && css`
        animation: ${waveform} 0.8s ease-in-out infinite;
        
        &:nth-child(1) { animation-delay: 0s; height: 8px; }
        &:nth-child(2) { animation-delay: 0.1s; height: 12px; }
        &:nth-child(3) { animation-delay: 0.2s; height: 16px; }
        &:nth-child(4) { animation-delay: 0.3s; height: 10px; }
        &:nth-child(5) { animation-delay: 0.4s; height: 6px; }
      `}
      
      ${props => !props.$hasRecording && css`
        height: 4px;
      `}
    }
  }
  
  .play-icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.$hasRecording ? 'rgba(24, 144, 255, 0.1)' : '#f5f5f5'};
    color: ${props => props.$hasRecording ? '#1890ff' : '#bfbfbf'};
    font-size: 12px;
    transition: all 0.2s;
    
    ${props => props.$hasRecording && css`
      &:hover {
        background: #1890ff;
        color: #fff;
        transform: scale(1.1);
      }
    `}
  }
`;

export const DateTimeCell = styled.div`
  .date {
    font-weight: 500;
    color: #262626;
    font-size: 13px;
  }
  
  .time {
    font-size: 12px;
    color: #8c8c8c;
  }
`;

// ============================================
// CALL DETAILS DRAWER
// ============================================

export const DetailsDrawer = styled(Drawer)`
  .ant-drawer-header {
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
  }
  
  .ant-drawer-body {
    padding: 0;
    background: #fafafa;
  }
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  
  .header-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    
    ${props => props.$direction === 'inbound' ? css`
      background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
      color: #fff;
    ` : css`
      background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
      color: #fff;
    `}
  }
  
  .header-content {
    flex: 1;
    
    .contact-name {
      font-size: 18px;
      font-weight: 600;
      color: #262626;
      margin-bottom: 4px;
    }
    
    .phone-number {
      font-size: 14px;
      color: #8c8c8c;
    }
  }
  
  .header-stats {
    display: flex;
    gap: 24px;
    
    .stat {
      text-align: center;
      
      .stat-value {
        font-size: 20px;
        font-weight: 700;
        color: #262626;
      }
      
      .stat-label {
        font-size: 12px;
        color: #8c8c8c;
      }
    }
  }
`;

export const DrawerSection = styled.div`
  background: #fff;
  margin: 12px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  .section-header {
    padding: 14px 16px;
    font-weight: 600;
    font-size: 14px;
    color: #262626;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: ${props => props.$collapsible ? 'pointer' : 'default'};
    
    .section-icon {
      margin-right: 10px;
      color: #8c8c8c;
    }
    
    .collapse-icon {
      color: #8c8c8c;
      transition: transform 0.2s;
      
      ${props => props.$collapsed && css`
        transform: rotate(-90deg);
      `}
    }
  }
  
  .section-content {
    padding: 16px;
    
    ${props => props.$collapsed && css`
      display: none;
    `}
  }
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  
  .info-item {
    .info-label {
      font-size: 12px;
      color: #8c8c8c;
      margin-bottom: 4px;
    }
    
    .info-value {
      font-size: 14px;
      color: #262626;
      font-weight: 500;
    }
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// ============================================
// TRANSCRIPT
// ============================================

export const TranscriptContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  
  .transcript-empty {
    text-align: center;
    padding: 40px 20px;
    color: #8c8c8c;
  }
`;

export const TranscriptEntry = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
  
  &:last-child {
    border-bottom: none;
  }
  
  .speaker-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    flex-shrink: 0;
    
    ${props => props.$speaker === 'agent' ? css`
      background: rgba(24, 144, 255, 0.1);
      color: #1890ff;
    ` : css`
      background: rgba(82, 196, 26, 0.1);
      color: #52c41a;
    `}
  }
  
  .transcript-content {
    flex: 1;
    
    .speaker-name {
      font-size: 12px;
      font-weight: 600;
      color: ${props => props.$speaker === 'agent' ? '#1890ff' : '#52c41a'};
      margin-bottom: 4px;
    }
    
    .transcript-text {
      font-size: 14px;
      color: #262626;
      line-height: 1.5;
    }
  }
  
  .transcript-time {
    font-size: 11px;
    color: #bfbfbf;
    flex-shrink: 0;
    cursor: pointer;
    
    &:hover {
      color: #1890ff;
      text-decoration: underline;
    }
  }
`;

// ============================================
// RECORDING PLAYER MODAL
// ============================================

export const PlayerModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
  }
  
  .ant-modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid #f0f0f0;
  }
  
  .ant-modal-body {
    padding: 0;
  }
`;

export const PlayerHeader = styled.div`
  padding: 20px 24px;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: #fff;
  
  .player-info {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
    
    .player-avatar {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
    }
    
    .player-details {
      flex: 1;
      
      .player-name {
        font-size: 16px;
        font-weight: 600;
      }
      
      .player-meta {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.6);
        margin-top: 2px;
      }
    }
    
    .player-duration {
      font-size: 24px;
      font-weight: 700;
      font-family: 'SF Mono', 'Consolas', monospace;
    }
  }
`;

export const WaveformContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  gap: 3px;
  padding: 0 20px;
  cursor: pointer;
  
  .waveform-bar {
    width: 4px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    transition: background 0.1s;
    
    &.played {
      background: #1890ff;
    }
    
    &.current {
      background: #fff;
    }
  }
`;

export const PlayerControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  
  .control-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: #f5f5f5;
    color: #595959;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    
    &:hover {
      background: #e8e8e8;
    }
    
    &.play-btn {
      width: 56px;
      height: 56px;
      background: #1890ff;
      color: #fff;
      font-size: 18px;
      
      &:hover {
        background: #40a9ff;
        transform: scale(1.05);
      }
    }
  }
  
  .progress-container {
    flex: 1;
    max-width: 400px;
    display: flex;
    align-items: center;
    gap: 12px;
    
    .progress-time {
      font-size: 12px;
      color: #8c8c8c;
      font-family: 'SF Mono', 'Consolas', monospace;
      min-width: 45px;
    }
    
    .ant-slider {
      flex: 1;
      margin: 0;
    }
  }
`;

export const SpeedControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  
  .speed-label {
    font-size: 12px;
    color: #8c8c8c;
    margin-right: 8px;
  }
  
  .speed-btn {
    padding: 4px 12px;
    border-radius: 6px;
    border: 1px solid #e8e8e8;
    background: #fff;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      border-color: #1890ff;
      color: #1890ff;
    }
    
    &.active {
      background: #1890ff;
      border-color: #1890ff;
      color: #fff;
    }
  }
`;

export const PlayerTabs = styled.div`
  .ant-tabs-nav {
    padding: 0 24px;
    margin: 0;
  }
  
  .ant-tabs-content {
    padding: 16px 24px;
    max-height: 300px;
    overflow-y: auto;
  }
`;

// ============================================
// ACTIVE CALLS TAB
// ============================================

export const ActiveCallCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 16px;
  border-left: 4px solid #52c41a;
  
  .call-avatar {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
  }
  
  .call-info {
    flex: 1;
    
    .call-name {
      font-weight: 600;
      font-size: 15px;
      color: #262626;
    }
    
    .call-number {
      font-size: 13px;
      color: #8c8c8c;
    }
  }
  
  .call-duration {
    font-size: 20px;
    font-weight: 700;
    color: #52c41a;
    font-family: 'SF Mono', 'Consolas', monospace;
  }
  
  .call-actions {
    display: flex;
    gap: 8px;
  }
  
  .live-indicator {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: rgba(82, 196, 26, 0.1);
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    color: #52c41a;
    
    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #52c41a;
      animation: ${pulse} 1.5s ease-in-out infinite;
    }
  }
`;

// ============================================
// EMPTY STATE
// ============================================

export const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  
  .empty-icon {
    font-size: 48px;
    color: #d9d9d9;
    margin-bottom: 16px;
  }
  
  .empty-title {
    font-size: 16px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 8px;
  }
  
  .empty-description {
    font-size: 14px;
    color: #8c8c8c;
    max-width: 300px;
    margin: 0 auto;
  }
`;

// ============================================
// ACTION BUTTONS
// ============================================

export const ActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 500;
  
  &.primary-action {
    background: ${COLORS.$primaryBlue || '#1890ff'};
    border-color: ${COLORS.$primaryBlue || '#1890ff'};
    
    &:hover {
      background: ${COLORS.$primaryBlueDark || '#096dd9'};
      border-color: ${COLORS.$primaryBlueDark || '#096dd9'};
    }
  }
`;

export const ExportButton = styled(Button)`
  border-radius: 8px;
  
  .anticon {
    margin-right: 6px;
  }
`;
